import React from "react";
import { Avatar, Box, Group, Menu, Text, UnstyledButton } from "@mantine/core";
import { DESIGN_ICONS } from "../../theme/designIcons";
import { useLocationSwitcher } from "../../hooks/useLocationSwitcher";
import { useConfirmLeave } from "../../context/NavigationGuardContext";
import { formatLocationAddress } from "../../utils/currentLocation";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import { FLYOUT_CLOSE_GRACE_MS, FLYOUT_OPEN_DELAY_MS } from "./useFlyoutHover";
import type { NavModel } from "./useNavModel";
import styles from "./AccountMenu.module.css";

type AccountMenuProps = Pick<
  NavModel,
  | "account"
  | "showLocationSwitcher"
  | "availableLocations"
  | "selectedSalonId"
  | "handleSignOut"
> & {
  onNavigate: (path: string) => void;
};

/**
 * The staff avatar at the bottom of the rail. Hovering (or clicking) it opens
 * the account menu that holds what used to sit in the rail: the location list
 * (multi-location staff), Settings and Log out.
 */
export const AccountMenu: React.FC<AccountMenuProps> = ({
  account,
  showLocationSwitcher,
  availableLocations,
  selectedSalonId,
  handleSignOut,
  onNavigate,
}) => {
  const { switchLocation, isSwitching } = useLocationSwitcher();
  // Switching location reloads the whole app, so it is an exit: unsaved
  // changes (e.g. a Reports view) get the Save / Discard prompt first.
  // Picking the current location is a no-op and never asks.
  const confirmLeave = useConfirmLeave();
  const { MapPin, Check, Settings, LogOut } = DESIGN_ICONS;
  const initials = account?.initials || "U";
  const subtitle = [account?.role, account?.salonName]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <Menu
        trigger="click-hover"
        openDelay={FLYOUT_OPEN_DELAY_MS}
        closeDelay={FLYOUT_CLOSE_GRACE_MS}
        position="right-end"
        offset={10}
        width={288}
        classNames={{
          dropdown: styles.dropdown,
          item: styles.item,
          label: styles.label,
          divider: styles.divider,
        }}
      >
        <Menu.Target>
          <UnstyledButton className={styles.trigger} aria-label="Account">
            <Avatar
              color="teal"
              variant="filled"
              radius="xl"
              size={34}
              className={styles.triggerAvatar}
            >
              {initials}
            </Avatar>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Group gap={10} wrap="nowrap" className={styles.header}>
            <Avatar color="teal" variant="filled" radius="xl" size={38}>
              {initials}
            </Avatar>
            <Box miw={0}>
              <Text fw={600} size="sm" truncate>
                {account?.name || "Signed in"}
              </Text>
              {subtitle && (
                <Text size="xs" c="dimmed" truncate title={subtitle}>
                  {subtitle}
                </Text>
              )}
            </Box>
          </Group>

          {showLocationSwitcher && (
            <>
              <Menu.Divider />
              <Menu.Label>Location</Menu.Label>
              {availableLocations.map((loc) => {
                const current = loc.salon_id === selectedSalonId;
                return (
                  <Menu.Item
                    key={loc.salon_id}
                    aria-current={current || undefined}
                    data-current={current || undefined}
                    disabled={isSwitching}
                    leftSection={<MapPin size={15} />}
                    rightSection={
                      current ? (
                        <Check size={15} className={styles.currentCheck} />
                      ) : null
                    }
                    onClick={() =>
                      current
                        ? switchLocation(loc.salon_id)
                        : confirmLeave(() => switchLocation(loc.salon_id))
                    }
                  >
                    <Text inherit lh={1.3}>
                      {loc.name}
                    </Text>
                    <Text size="xs" c="dimmed" fw={400} truncate>
                      {formatLocationAddress(loc)}
                    </Text>
                  </Menu.Item>
                );
              })}
            </>
          )}

          <Menu.Divider />
          <Menu.Item
            leftSection={<Settings size={15} />}
            onClick={() => onNavigate("/admin/settings/studio")}
          >
            Settings
          </Menu.Item>
          <Menu.Item
            data-destructive
            leftSection={<LogOut size={15} />}
            onClick={handleSignOut}
          >
            Log out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      {/* Brief PATCH → full reload window after picking a location. */}
      <BrandedLoadingOverlay
        visible={isSwitching}
        style={{ position: "fixed", zIndex: 3000 }}
      />
    </>
  );
};
